import { Component, EventEmitter, Input, Output, OnInit, OnChanges } from '@angular/core';
import * as _ from 'lodash';
import { ORG_HEIRARCHY_MODEL } from './models/org-heirarchy.modifier';
import { TEnhancedOrgHeirachyModel, TOrgHeirachyModel } from './models/org-heirarchy.types';
import { PopoverController } from '@ionic/angular';
import { TOrgHeirachy, TOrgHeirachyTreeNode } from '@monorepo/types';
import { TranslateService } from '@ngx-translate/core';

export type TSelectedHeirarchy = {
  payload: TOrgHeirachy;
  leafNodesSelected: number;
};

export function getLeafNodeCount(orgTree: TOrgHeirachy) {
  let count = 0;
  //recursive loop to traverse tree and increase count for leaf nodes
  const rec = (nodes: TOrgHeirachyTreeNode[]) =>
    nodes.map((node) => {
      if (node.children) rec(node.children.nodes);
      else count = count + 1;
    });

  rec(orgTree.values);
  return count;
}

@Component({
  selector: 'monorepo-org-heirarchy',
  templateUrl: './org-heirarchy.component.html',
  styleUrls: ['./org-heirarchy.component.scss'],
})
export class OrgHeirarchyComponent implements OnInit, OnChanges {
  //props
  @Input() orgTree!: TOrgHeirachy;
  @Input() filteredOrgTree?: TOrgHeirachy;

  //optional props
  @Input() title = 'Org hierarchy';
  @Input() showDesc = true;
  @Input() isPopover = true;
  @Input() showClear = false;
  @Input() showApply = true;
  @Input() hasMultiSelectSupport = true;
  @Input() showActiveNode = false;
  @Input() minimizeAllNodes = true;

  //component related data
  @Input() enhancedOrgTreeModel?: TEnhancedOrgHeirachyModel;
  @Output() OnNodeSelection = new EventEmitter<TEnhancedOrgHeirachyModel['selectedNode']>();
  @Output() orgHeirarchySelectionEmitter = new EventEmitter<TSelectedHeirarchy>();

  constructor(private popoverController: PopoverController, private translate: TranslateService) {}

  ngOnInit(): void {
    this.setEnhancedOrgTreeModel();
  }

  ngOnChanges(): void {
    this.setEnhancedOrgTreeModel();
  }

  setEnhancedOrgTreeModel() {
    this.enhancedOrgTreeModel = ORG_HEIRARCHY_MODEL.createEnhancedModel(
      this.minimizeAllNodes,
      this.orgTree,
      this.filteredOrgTree,
    );
  }

  collapseExpandNode(treeNode: TOrgHeirachyModel['values'][number]) {
    if (this.enhancedOrgTreeModel) {
      this.enhancedOrgTreeModel = ORG_HEIRARCHY_MODEL.toggleNode(treeNode, this.enhancedOrgTreeModel);
    }
  }

  onNodeSelection(treeNode: TOrgHeirachyModel['values'][number]) {
    if (this.enhancedOrgTreeModel) {
      this.enhancedOrgTreeModel = ORG_HEIRARCHY_MODEL.selectNode(
        treeNode,
        this.enhancedOrgTreeModel,
        this.hasMultiSelectSupport,
      );
      if (!this.hasMultiSelectSupport) {
        this.OnNodeSelection.emit(this.enhancedOrgTreeModel.selectedNode);
      }

      if (!this.showApply) {
        this.onApply();
      }
    }
  }

  onClear() {
    const data: TSelectedHeirarchy = {
      payload: {
        ...this.orgTree,
        values: [],
      },
      leafNodesSelected: 0,
    };
    if (this.isPopover) {
      this.popoverController.dismiss(data, 'ON_CLEAR');
    } else {
      this.orgHeirarchySelectionEmitter.emit(data);
    }
  }

  onApply() {
    if (this.enhancedOrgTreeModel?.payload) {
      const data: TSelectedHeirarchy = {
        payload: this.enhancedOrgTreeModel.payload,
        leafNodesSelected: getLeafNodeCount(this.enhancedOrgTreeModel.payload),
      };
      if (this.isPopover) {
        this.popoverController.dismiss(data, 'ON_APPLY');
      } else {
        this.orgHeirarchySelectionEmitter.emit(data);
      }
    } else if (!this.showApply) {
      this.onClear();
    }
  }
}
